import { useEffect, useMemo, useState } from 'react';
import { useRows } from '@/hooks/useLive';
import { save, softDelete, upsert } from '@/lib/crud';
import { num, isoDate } from '@/lib/format';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { ConfirmRemove } from '@/components/ui/ConfirmRemove';
import { StatusChip } from '@/components/ui/StatusChip';
import {
  NumberInput, SelectInput, TextArea, TextInput,
} from '@/components/ui/Field';

interface PoLine extends Record<string, unknown> {
  id: string;
  po_id: string;
  part_id: string;
  qty_ordered: number;
  qty_received: number;
  unit_cost: number | null;
  expected_date: string | null;
}

const STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'requested', label: 'Requested' },
  { value: 'approved', label: 'Approved' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'partially_received', label: 'Partially received' },
  { value: 'received', label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' },
];

/**
 * Receiving a line here posts the stock receipt automatically — the database
 * trigger writes the ledger entry, so nobody has to remember to book stock in
 * after signing for a delivery.
 */
export function PurchaseOrderDrawer({
  record, onClose, locked,
}: { record: Record<string, unknown>; onClose: () => void; locked?: string | null }) {
  const isNew = !record.id;
  const suppliers = useRows<Record<string, unknown>>('suppliers', 'name');
  const parts = useRows<Record<string, unknown>>('parts', 'part_no');
  const allLines = useRows<PoLine>('purchase_order_lines');

  const [v, setV] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const [newPart, setNewPart] = useState<string | null>(null);
  const [newQty, setNewQty] = useState<number | null>(null);
  const [newCost, setNewCost] = useState<number | null>(null);

  useEffect(() => {
    setV({
      status: 'draft',
      currency: 'NAD',
      order_date: isoDate(),
      ...record,
    });
  }, [record]);

  const set = (k: string, val: unknown) => setV((d) => ({ ...d, [k]: val }));

  const lines = useMemo(
    () => (allLines ?? []).filter((l) => l.po_id === record.id),
    [allLines, record.id],
  );

  const total = lines.reduce((s, l) => s + Number(l.qty_ordered) * Number(l.unit_cost ?? 0), 0);
  const partLabel = (id: string) => {
    const p = (parts ?? []).find((r) => String(r.id) === id);
    return p ? `${p.part_no} — ${p.description}` : '—';
  };

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!String(v.po_no ?? '').trim()) e.po_no = 'Order number is required';
    if (v.expected_date && v.order_date && String(v.expected_date) < String(v.order_date)) {
      e.expected_date = 'Expected before it was ordered';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setBusy(true);
    const payload = { ...v };
    if (record.id) payload.id = record.id;
    const saved = await save(
      () => upsert('purchase_orders', payload as Record<string, unknown> & { id?: string }),
      'Purchase order saved',
    );
    setBusy(false);
    if (saved) onClose();
  }

  async function addLine() {
    if (!newPart || !newQty || !record.id) return;
    setBusy(true);
    await save(
      () => upsert('purchase_order_lines', {
        po_id: record.id as string,
        part_id: newPart,
        qty_ordered: newQty,
        qty_received: 0,
        unit_cost: newCost,
      }),
      'Line added',
    );
    setNewPart(null);
    setNewQty(null);
    setNewCost(null);
    setBusy(false);
  }

  async function receive(line: PoLine, qty: number) {
    setBusy(true);
    await save(
      // The whole line, not just the quantity: the save is an upsert, and
      // Postgres checks the required columns of the proposed row before it
      // finds the existing one — sending only id and qty_received was
      // refused ("null value in column po_id"), so no receipt was ever posted.
      () => upsert('purchase_order_lines', { ...line, qty_received: qty } as Record<string, unknown> & { id: string }),
      qty >= Number(line.qty_ordered) ? 'Line received in full — stock posted' : 'Receipt posted to stock',
    );
    setBusy(false);
  }

  return (
    <Drawer
      open
      locked={locked}
      width={620}
      title={isNew ? 'New purchase order' : String(v.po_no ?? 'Purchase order')}
      subtitle={isNew ? undefined : `${lines.length} line${lines.length === 1 ? '' : 's'} · ${num(total, 2)}`}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <ConfirmRemove
              disabled={busy}
              label="Remove"
              question="Remove this order?"
              onConfirm={async () => {
                const ok = await save(() => softDelete('purchase_orders', record.id as string), 'Removed');
                // Close only if it went through. A refusal leaves the record
                // open with the reason on screen, rather than closing over it.
                if (ok !== null) onClose();
                return ok !== null;
              }}
            />
          )}
          <span className="spacer" />
          <Button variant="secondary" onClick={onClose} disabled={busy}>Close</Button>
          <Button onClick={submit} loading={busy}>Save</Button>
        </>
      }
    >
      <div className="form-grid">
        <TextInput id="po-no" label="Order number" required error={errors.po_no}
          value={String(v.po_no ?? '')} onChange={(x) => set('po_no', x)} />
        <SelectInput id="po-status" label="Status" value={String(v.status ?? 'draft')}
          options={STATUSES} placeholder="" onChange={(x) => set('status', x ?? 'draft')} />

        <SelectInput id="po-sup" label="Supplier" value={(v.supplier_id as string) ?? null}
          options={(suppliers ?? []).map((s) => ({ value: String(s.id), label: String(s.name) }))}
          onChange={(x) => set('supplier_id', x)} />
        <TextInput id="po-cur" label="Currency" value={String(v.currency ?? 'NAD')}
          onChange={(x) => set('currency', x)} />

        <TextInput id="po-ordered" label="Order date" type="date"
          value={String(v.order_date ?? '')} onChange={(x) => set('order_date', x || null)} />
        <TextInput id="po-exp" label="Expected" type="date" error={errors.expected_date}
          value={String(v.expected_date ?? '')} onChange={(x) => set('expected_date', x || null)}
          help="Drives the ETA shown against a part that is holding up a repair." />

        <div className="field--full">
          <TextArea id="po-notes" label="Notes" value={String(v.notes ?? '')}
            onChange={(x) => set('notes', x || null)} />
        </div>
      </div>

      {isNew ? (
        <p className="small muted" style={{ marginTop: 'var(--sp-3)' }}>
          Save the order first — lines are added to it once it exists.
        </p>
      ) : (
        <>
          <h4 style={{ margin: 'var(--sp-4) 0 var(--sp-2)' }}>Lines</h4>
          <div className="col">
            {lines.length === 0 && <p className="small muted">No lines on this order yet.</p>}
            {lines.map((l) => {
              const outstanding = Number(l.qty_ordered) - Number(l.qty_received);
              return (
                <div className="issue issue--ok" key={l.id}
                  style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ minWidth: 200 }}>{partLabel(l.part_id)}</span>
                  <span className="small tabular">
                    {num(l.qty_received, 0)} of {num(l.qty_ordered, 0)}
                    {l.unit_cost != null ? ` @ ${num(l.unit_cost, 2)}` : ''}
                  </span>
                  <span className="spacer" />
                  {outstanding <= 0 ? (
                    <StatusChip tone="green">Received</StatusChip>
                  ) : (
                    <Button variant="secondary" disabled={busy}
                      onClick={() => receive(l, Number(l.qty_ordered))}>
                      Receive {num(outstanding, 0)}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="form-grid" style={{ marginTop: 'var(--sp-3)' }}>
            <div className="field--full">
              <SelectInput id="po-newpart" label="Add a part" value={newPart}
                options={(parts ?? []).map((p) => ({
                  value: String(p.id), label: `${p.part_no} — ${p.description}`,
                }))}
                onChange={(x) => {
                  setNewPart(x);
                  const p = (parts ?? []).find((r) => String(r.id) === x);
                  if (p?.unit_cost != null && newCost == null) setNewCost(Number(p.unit_cost));
                }} />
            </div>
            <NumberInput id="po-newqty" label="Quantity" min={0} step={1}
              value={newQty} onChange={setNewQty} />
            <NumberInput id="po-newcost" label="Unit cost" min={0} step={10}
              value={newCost} onChange={setNewCost} />
            <div className="field--full">
              <Button variant="secondary" disabled={!newPart || !newQty || busy} onClick={addLine}>
                Add line
              </Button>
            </div>
          </div>

          <p className="small muted" style={{ marginTop: 'var(--sp-3)', marginBottom: 0 }}>
            Receiving a line posts the stock receipt to the ledger automatically.
          </p>
        </>
      )}
    </Drawer>
  );
}
